import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ConfigService } from 'src/app/service/config.service';
import { TokenService } from 'src/app/shared/services/customer-api/token.service';

/**
 * Centralizes Bearer token injection and 401 handling for all HTTP requests.
 *
 * Token selection by URL:
 * - Customer/Reporting/Newsletter/ChatLLM APIs -> customer `token`
 * - Data/Metadata/Quant APIs -> `tokenDataAccess` (but skipped if header already set by ApiBaseService)
 * - Unknown URLs (assets, Sentry, etc.) -> no token added
 *
 * Requests that already have an Authorization header are passed through unchanged.
 * This ensures ApiBaseService (which sets its own auth for Data API) is not affected.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private knownApiUrls: { url: string; tokenType: 'customer' | 'data' }[] = [];
  private initialized = false;

  constructor(
    private tokenService: TokenService,
    private config: ConfigService,
    private router: Router,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    this.initUrls();

    let request = req;
    const knownApi = this.findKnownApi(req.url);

    const headersToSet: Record<string, string> = {};
    if (knownApi?.tokenType === 'customer') {
      // YT-9962: prevent stale lists after CRUD. Customer-token APIs only —
      // these headers are not CORS-safelisted and break preflights on data-API
      // gateways with a fixed allow-headers list (e.g. UBS on-prem).
      headersToSet['Cache-Control'] = 'no-cache';
      headersToSet['Pragma'] = 'no-cache';
    }

    if (!req.headers.has('Authorization') && knownApi) {
      const token = this.resolveToken(knownApi);
      if (token) {
        headersToSet['Authorization'] = `Bearer ${token}`;
      }
    }

    if (Object.keys(headersToSet).length > 0) {
      request = req.clone({ setHeaders: headersToSet });
    }

    let response$ = next.handle(request);

    // Retry GET requests on 5xx/network errors (up to 3 times)
    if (req.method === 'GET') {
      response$ = response$.pipe(
        retry({
          count: 3,
          delay: (error: HttpErrorResponse) => {
            if (error.status >= 500 || error.status === 0) {
              return timer(1000);
            }
            return throwError(() => error);
          },
        }),
      );
    }

    return response$.pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !req.url.includes('/login')) {
          this.tokenService.clear();
          this.router.navigate(['/login']);
        }
        return throwError(() => error);
      }),
    );
  }

  private findKnownApi(
    url: string,
  ): { url: string; tokenType: 'customer' | 'data' } | null {
    return this.knownApiUrls.find((entry) => url.startsWith(entry.url)) ?? null;
  }

  private resolveToken(entry: {
    tokenType: 'customer' | 'data';
  }): string | null {
    if (entry.tokenType === 'customer') {
      return this.tokenService.getActiveToken();
    }
    // Data API token — only used if ApiBaseService didn't set Authorization
    return this.tokenService.tokenDataAccess || null;
  }

  private initUrls(): void {
    if (this.initialized) return;

    const cfg = this.config.appConfig;
    if (!cfg) return;

    this.initialized = true;

    // Customer-token APIs
    const customerUrls = [
      cfg.urlCustomerApi,
      cfg.urlReportingServiceApi,
      cfg.urlNewsletterApi,
      cfg.urlChatLLMApi,
    ];

    // Data-token APIs
    const dataUrls = [cfg.urlDataApi, cfg.urlMetadataApi, cfg.urlQuantApi];

    for (const url of customerUrls) {
      if (url) this.knownApiUrls.push({ url, tokenType: 'customer' });
    }
    for (const url of dataUrls) {
      if (url) this.knownApiUrls.push({ url, tokenType: 'data' });
    }
  }
}
